/* eslint-disable indent */
import React, { useState, useMemo, useEffect } from 'react';

import Sidebar from './components/Sidebar/Sidebar';
import Main from './components/Main/Main';

import {
  useFetchAbout,
  useFetchExhibitions,
  useFetchEvents,
  useFetchStaticText,
  useFetchLanguage,
  useMedia,
  useFetchEducation,
  useFetchPodcast,
  useFetchPublication,
} from './utils/hooks';

import './App.scss';
import { getCurrentExhibitions } from './utils/getCurrentExhibitions';
import { getUpcomingExhibitions } from './utils/getUpcomingExhibitions';
import { getArchiveExhibitions } from './utils/getArchiveExhibitions';
import { getCurrentEvents } from './utils/getCurrentEvents';
import { getPreviousEvents } from './utils/getPreviousEvents';
import { getUpcomingEvents } from './utils/getUpcomingEvents';

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [language, setLanguage] = useFetchLanguage();
  const [exhibitions, exhibitionsError] = useFetchExhibitions(language);
  const [events, eventsError] = useFetchEvents(language);
  const [education, educationError] = useFetchEducation(language);
  const [podcast, podcastError] = useFetchPodcast(language);
  const [publication, publicationError] = useFetchPublication(language);
  const [about, aboutError] = useFetchAbout(language);
  const [staticText, staticTextError] = useFetchStaticText(language);

  const currentExhibitions = useMemo(
    () => getCurrentExhibitions(exhibitions),
    [exhibitions],
  );
  const archiveExhibitions = useMemo(
    () => getArchiveExhibitions(exhibitions),
    [exhibitions],
  );
  const upcomingExhibitions = useMemo(
    () => getUpcomingExhibitions(exhibitions),
    [exhibitions],
  );
  const currentEvents = useMemo(() => getCurrentEvents(events), [events]);
  const previousEvents = useMemo(() => getPreviousEvents(events), [events]);
  const upcomingEvents = useMemo(() => getUpcomingEvents(events), [events]);

  const isMobileVersion = useMedia(
    ['(min-width: 768px)', '(min-width: 280px)'],
    [false, true],
  );

  useEffect(() => {
    setIsSidebarOpen(isMobileVersion);
  }, [isMobileVersion]);

  return (
    <>
      {(!!events.length || !!exhibitions.length) && (
        <>
          {!eventsError &&
          !aboutError &&
          !staticTextError &&
          !exhibitionsError &&
          !educationError &&
          !podcastError &&
          !publicationError ? (
            <Sidebar
              events={events}
              exhibitions={exhibitions}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              setActiveSection={setActiveSection}
              currentExhibitions={currentExhibitions}
              upcomingExhibitions={upcomingExhibitions}
              archiveExhibitions={archiveExhibitions}
              upcomingEvents={upcomingEvents}
              educationItems={education}
              podcastItems={podcast}
              publicationItems={publication}
              language={language}
              setLanguage={setLanguage}
              staticText={staticText}
            >
              <Main
                events={events}
                exhibitions={exhibitions}
                about={about}
                setIsSidebarOpen={setIsSidebarOpen}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                currentExhibitions={currentExhibitions}
                upcomingExhibitions={upcomingExhibitions}
                archiveExhibitions={archiveExhibitions}
                currentEvents={currentEvents}
                previousEvents={previousEvents}
                upcomingEvents={upcomingEvents}
                education={education}
                podcast={podcast}
                publication={publication}
                language={language}
                setLanguage={setLanguage}
                staticText={staticText}
              />
            </Sidebar>
          ) : (
            <div className="error-message">
              <h1>OOps</h1>
              <h2>
                Something went wrong. We are working on getting this fixed as
                soon as we can. You may be able to try again
              </h2>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default App;
